import type { ChangeEvent } from 'react';

import { Input } from '@/components/ui/input';

import type { QuestionRendererProps } from '../types';

const buildPlaceholder = (allowInternational: boolean | undefined, defaultCountry?: string) => {
  if (allowInternational) {
    return '+1 555 000 0000';
  }
  if (defaultCountry) {
    return `(${defaultCountry.toUpperCase()}) 000-0000`;
  }
  return '(555) 000-0000';
};

export const PhoneNumberQuestion = ({
  question,
  value,
  onChange,
  onBlur,
  error,
  mode,
  disabled,
}: QuestionRendererProps<string>) => {
  if (question.type !== 'phone_number') {
    return null;
  }

  const isInteractive = mode === 'runtime' && !disabled;
  const placeholder = buildPlaceholder(question.allowInternational, question.defaultCountry ?? undefined);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!isInteractive) {
      return;
    }
    onChange?.(event.target.value);
  };

  const hints: string[] = [];
  if (question.defaultCountry) {
    hints.push(`Default country code: ${question.defaultCountry.toUpperCase()}`);
  }
  if (question.allowInternational) {
    hints.push('Supports international formatting');
  } else {
    hints.push('International numbers are not accepted');
  }

  return (
    <div className="space-y-1.5">
      <Input
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        value={value ?? ''}
        onChange={handleChange}
        onBlur={() => onBlur?.()}
        placeholder={placeholder}
        disabled={!isInteractive}
        aria-invalid={Boolean(error)}
        aria-describedby={`validation-${question.id}`}
        aria-errormessage={error ? `error-${question.id}` : undefined}
      />
      <ul className="space-y-0.5 text-xs text-muted-foreground">
        {hints.map((hint) => (
          <li key={hint}>{hint}</li>
        ))}
      </ul>
    </div>
  );
};
